import { useState, useEffect } from 'react';
import AuthService from '../services/AuthService';

const authService = new AuthService();

const SUBSCRIPTION_HIERARCHY = {
  simple: 1,
  advanced: 2,
  professional: 3
};

export interface SubscriptionInfo {
  planType?: string;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  roles?: string[];
  hasActiveSubscription: boolean;
  isSimple: boolean;
  isAdvanced: boolean;
  isProfessional: boolean;
  isAdmin: boolean;
  // Free tier usage tracking
  imageCount: number;
  imageLimit: number;
  remaining: number;
  canUploadMore: boolean;
  isFreeUser: boolean;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    hasActiveSubscription: false,
    isSimple: false,
    isAdvanced: false,
    isProfessional: false,
    isAdmin: false,
    imageCount: 0,
    imageLimit: 15,
    remaining: 15,
    canUploadMore: true,
    isFreeUser: true
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionInfo();
    refreshUsage();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const user = await authService.getUser();
      
      if (!user || !user.profile) {
        setSubscription({
          hasActiveSubscription: false,
          isSimple: false,
          isAdvanced: false,
          isProfessional: false,
          isAdmin: false,
          imageCount: 0,
          imageLimit: 15,
          remaining: 15,
          canUploadMore: true,
          isFreeUser: true
        });
        setLoading(false);
        return;
      }

      const planType = user.profile.planType as string | undefined;
      const subscriptionStatus = user.profile.subscriptionStatus as string | undefined;
      const subscriptionEndDate = user.profile.subscriptionEndDate as string | undefined;
      
      // Extract roles from profile
      const rolesClaim = user.profile.role;
      const roles = Array.isArray(rolesClaim) ? rolesClaim : (rolesClaim ? [rolesClaim] : []);
      const isAdmin = roles.includes('admin') || roles.includes('Admin');

      const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
      const userLevel = SUBSCRIPTION_HIERARCHY[planType as keyof typeof SUBSCRIPTION_HIERARCHY] || 0;

      setSubscription(prev => ({
        ...prev,
        planType,
        subscriptionStatus,
        subscriptionEndDate,
        roles,
        hasActiveSubscription: isActive,
        isSimple: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.simple,
        isAdvanced: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.advanced,
        isProfessional: isActive && userLevel >= SUBSCRIPTION_HIERARCHY.professional,
        isAdmin,
        isFreeUser: !isActive
      }));
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setLoading(false);
    }
  };

  const hasSubscription = (requiredPlan: 'simple' | 'advanced' | 'professional'): boolean => {
    if (!subscription.hasActiveSubscription) return false;
    
    const userLevel = SUBSCRIPTION_HIERARCHY[subscription.planType as keyof typeof SUBSCRIPTION_HIERARCHY] || 0;
    const requiredLevel = SUBSCRIPTION_HIERARCHY[requiredPlan];
    
    return userLevel >= requiredLevel;
  };

  const refreshSubscription = async () => {
    setLoading(true);
    // First refresh the claims from IdentityServer
    const refreshed = await authService.refreshUserClaims();
    if (refreshed) {
      // Then reload the subscription info from the refreshed claims
      await loadSubscriptionInfo();
    } else {
      setLoading(false);
    }
  };

  const refreshUsage = async () => {
    try {
      const token = await authService.getAccessToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(`${API_BASE_URL}/patients/user/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch usage:', response.status);
        return;
      }

      const usageData = await response.json();
      
      setSubscription(prev => ({
        ...prev,
        imageCount: usageData.imageCount || 0,
        imageLimit: usageData.limit || 15,
        remaining: usageData.remaining || 15,
        canUploadMore: usageData.canUpload !== false,
        isFreeUser: !usageData.hasSubscription
      }));
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  return {
    ...subscription,
    loading,
    hasSubscription,
    refresh: refreshSubscription,
    refreshUsage
  };
}
